import React, { useRef, useState } from 'react';
import { RawTrader, TraderCardData } from './types';
import { series, rng, hash } from './constants';

interface ExpertTraderCardProps {
  trader: RawTrader;
  card: TraderCardData;
  onSelect: () => void;
}

export const ExpertTraderCard: React.FC<ExpertTraderCardProps> = ({ trader, card, onSelect }) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);

  const [tooltipData, setTooltipData] = useState<{
    visible: boolean;
    xPercent: number;
    yPercent: number;
    val: string;
    date: string;
  }>({
    visible: false,
    xPercent: 50,
    yPercent: 50,
    val: '0%',
    date: '2026.09.09',
  });

  const s = series(trader);
  const minVal = Math.min(...s, 0);
  const maxVal = Math.max(...s, 0.1);

  // Generate SVG smooth path and area
  const svgPath = s
    .map((v, idx) => {
      const x = (idx / (s.length - 1)) * 100;
      const y = 35 - ((v - minVal) / (maxVal - minVal)) * 28;
      return `${idx === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(' ');

  const svgArea = `${svgPath} L100,40 L0,40 Z`;

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!chartContainerRef.current) return;
    const rect = chartContainerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const width = rect.width;

    let percentX = x / width;
    percentX = Math.max(0, Math.min(1, percentX));

    const index = Math.min(Math.floor(percentX * (s.length - 1)), s.length - 1);
    const v = s[index] || 0;
    const yPercent = 88 - ((v - minVal) / (maxVal - minVal)) * 70;

    const today = new Date('2026-09-09');
    const simulatedDate = new Date(today);
    simulatedDate.setDate(today.getDate() - trader.days + percentX * trader.days);

    const yyyy = simulatedDate.getFullYear();
    const mm = String(simulatedDate.getMonth() + 1).padStart(2, '0');
    const dd = String(simulatedDate.getDate()).padStart(2, '0');
    const dateStr = `${yyyy}.${mm}.${dd}`;

    setTooltipData({
      visible: true,
      xPercent: percentX * 100,
      yPercent,
      val: (v >= 0 ? '+' : '') + v.toFixed(2) + '%',
      date: dateStr,
    });
  };

  const handleMouseLeave = () => {
    setTooltipData((prev) => ({ ...prev, visible: false }));
  };

  const getStageBadge = (stage: string) => {
    switch (stage) {
      case 'Funded':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'Verification':
        return 'bg-teal-50 text-teal-700 border-teal-200';
      case 'Challenge':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const seed = hash(trader.name);
  const r = rng(seed);
  const rating = 4.2 + (r() * 0.7);
  const reviewsCount = Math.round(15 + r() * 120);
  const copiersCount = Math.round(20 + r() * 450);
  const copyPrice = trader.cap >= 200000 ? 50 : 30;

  const reliabilityBars = trader.risk >= 85 ? 5 : trader.risk >= 65 ? 4 : 2;
  const algoPercent = Math.round(40 + r() * 55);
  const algoBars = algoPercent >= 80 ? 5 : algoPercent >= 60 ? 4 : 3;

  const avatarUrl = `https://i.pravatar.cc/150?img=${(seed % 60) + 1}`;
  const flagCode = trader.country === 'VN' ? 'vn' : 'us';

  return (
    <div
      onClick={onSelect}
      className="bg-white !rounded-[14px] flex flex-col border border-gray-200 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 overflow-hidden group cursor-pointer"
    >
      {/* Card Header: Author Info */}
      <div className="px-3 pt-3 flex justify-between items-start">
        <div className="flex items-center space-x-3">
          <div className="relative flex-shrink-0">
            <img
              src={avatarUrl}
              alt={trader.name}
              className="w-12 h-12 rounded-full border border-gray-200 object-cover"
              onError={(e) => {
                (e.currentTarget as HTMLElement).style.display = 'none';
              }}
            />
            <div className="absolute -bottom-1 -right-1 bg-white p-[2px] rounded-full">
              <img
                src={`https://flagcdn.com/w20/${flagCode}.png`}
                alt={trader.country}
                className="w-4 h-4 object-cover rounded-full border border-gray-100"
              />
            </div>
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-gray-900 text-[15px] truncate pr-2 group-hover:text-blue-600 transition-colors">
              {trader.name}
            </div>
            <div className="flex items-center text-[11px] mt-0.5">
              <div className="flex text-[#facc15] space-x-[1px]">
                {[1, 2, 3, 4, 5].map((star) => (
                  <svg
                    key={star}
                    className={`w-3 h-3 fill-current ${
                      star <= Math.round(rating) ? 'text-[#facc15]' : 'text-gray-300'
                    }`}
                    viewBox="0 0 20 20"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                  </svg>
                ))}
              </div>
              <span className="text-gray-500 ml-1.5">
                {rating.toFixed(2)} ({reviewsCount})
              </span>
            </div>
          </div>
        </div>

        <div className="text-[#22c55e] flex-shrink-0" title="Đã xác minh bởi LUMIR">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
            <path
              fillRule="evenodd"
              d="M8.603 3.799A4.49 4.49 0 0112 2.25c1.357 0 2.573.6 3.397 1.549a4.49 4.49 0 013.498 1.307 4.491 4.491 0 011.307 3.497A4.49 4.49 0 0121.75 12a4.49 4.49 0 01-1.549 3.397 4.491 4.491 0 01-1.307 3.497 4.491 4.491 0 01-3.497 1.307A4.49 4.49 0 0112 21.75a4.49 4.49 0 01-3.397-1.549 4.49 4.49 0 01-3.498-1.306 4.491 4.491 0 01-1.307-3.498A4.49 4.49 0 012.25 12c0-1.357.6-2.573 1.549-3.397a4.49 4.49 0 011.307-3.497 4.49 4.49 0 013.497-1.307zm7.007 6.387a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      </div>

      {/* Strategy Title & Growth */}
      <div className="px-5 mt-4 flex justify-between items-start gap-4">
        <div className="flex flex-col gap-1">
          <div className="text-[#3b82f6] font-medium text-[16px] leading-snug line-clamp-1">
            {trader.tags.join(' • ')}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <span
              className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold border ${getStageBadge(
                trader.stage
              )}`}
            >
              {trader.stage}
            </span>
            <span className="text-gray-400 text-xs">· Quy mô {card.capStr}</span>
          </div>
        </div>
        <div className="text-right flex-shrink-0 flex flex-col justify-end h-full mt-1">
          <div
            style={{ color: card.retColor }}
            className="font-bold text-[22px] leading-none text-right font-mono"
          >
            {card.retStr}
          </div>
          <div className="text-gray-400 text-[9px] mt-1 font-medium whitespace-nowrap text-right">
            từ {trader.since} ({trader.days} ngày)
          </div>
        </div>
      </div>

      {/* Chart Canvas with Interactive Tooltip */}
      <div
        ref={chartContainerRef}
        className="h-[100px] w-full relative mt-4 mb-2 group/chart cursor-crosshair"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="w-full h-full">
          <defs>
            <linearGradient id={`grad-trader-${trader.name.replace(/\s+/g, '')}`} x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" style={{ stopColor: 'rgba(59, 130, 246, 0.16)', stopOpacity: 1 }} />
              <stop offset="100%" style={{ stopColor: 'rgba(59, 130, 246, 0)', stopOpacity: 1 }} />
            </linearGradient>
          </defs>
          <path d={svgArea} fill={`url(#grad-trader-${trader.name.replace(/\s+/g, '')})`} />
          <path
            d={svgPath}
            fill="none"
            stroke="#3b82f6"
            strokeWidth="0.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>

        {/* Dynamic Tooltip */}
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute w-[6px] h-[6px] bg-[#3b82f6] rounded-full -translate-x-1/2 -translate-y-1/2 transition-opacity duration-100 shadow-[0_0_0_2px_white]"
            style={{
              left: `${tooltipData.xPercent}%`,
              top: `${tooltipData.yPercent}%`,
              opacity: tooltipData.visible ? 1 : 0,
            }}
          />
          <div
            className="absolute bg-white shadow-[0_2px_10px_rgba(0,0,0,0.15)] rounded p-1.5 text-center transition-opacity duration-100 -translate-x-1/2 -translate-y-[calc(100%+12px)] min-w-[70px] border border-gray-100 z-20"
            style={{
              left: `${tooltipData.xPercent}%`,
              top: `${tooltipData.yPercent}%`,
              opacity: tooltipData.visible ? 1 : 0,
            }}
          >
            <div className="font-bold text-gray-800 text-[11px] leading-tight">{tooltipData.val}</div>
            <div className="text-gray-500 text-[9px] leading-tight mt-0.5">{tooltipData.date}</div>
          </div>
        </div>
      </div>

      {/* Card Stats: Reliability, Algo, Copiers */}
      <div className="px-4 py-3.5 flex items-center justify-between text-[11px] text-gray-500 border-t border-gray-100 bg-white">
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <div className="flex gap-[2px]">
            {[1, 2, 3, 4, 5].map((b) => (
              <div
                key={b}
                className={`w-1.5 h-3 rounded-[1px] ${
                  b <= reliabilityBars ? 'bg-[#22c55e]' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>
          <span className="whitespace-nowrap">Độ tin cậy</span>
        </div>

        <div className="flex items-center gap-1.5 flex-shrink-0 mx-1">
          <div className="flex gap-[2px]">
            {[1, 2, 3, 4, 5].map((b) => (
              <div
                key={b}
                className={`w-1.5 h-3 rounded-[1px] ${
                  b <= algoBars ? 'bg-[#3b82f6]' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>
          <span className="whitespace-nowrap">{algoPercent}% WinRate</span>
        </div>

        <div className="flex items-center gap-1 flex-shrink-0 text-gray-600">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-3.5 w-3.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
            />
          </svg>
          <span className="font-semibold">{copiersCount}</span>
        </div>
      </div>

      {/* Action Footer */}
      <div className="w-full bg-white group-hover:bg-blue-50 transition-colors duration-300 border-t border-gray-100 p-3.5 text-center flex items-center justify-center gap-2">
        <span className="text-[#3b82f6] text-[13px] font-medium">
          Xem hồ sơ &amp; Sao chép ({copyPrice}$/tháng)
        </span>
        <span className="text-[#3b82f6] text-xs transition-transform group-hover:translate-x-1">→</span>
      </div>
    </div>
  );
};
